import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { db, collection, query, where, orderBy, doc, getDoc, getDocs, getCountFromServer, limit, startAfter, isFirebaseReady, functions, httpsCallable } from '../../firebase';
import { getCachedDoc, getCachedQuery } from '../../utils/docCache';
import { whenSafeForCallables } from '../../utils/fcmToken';
import Loader from '../common/Loader';


import { FaGamepad, FaStar, FaFire, FaArrowRight, FaStore, FaShoppingCart } from 'react-icons/fa';
import BrandBadge from '../common/BrandBadge';
import { useLanguage } from '../../contexts/LanguageContext';

// Sub-components
import HeroSection from '../home/HeroSection';
import ActivityTicker from '../home/ActivityTicker';
import SponsorBand from '../home/SponsorBand';
import PartnerBand from '../home/PartnerBand';
// import PromoBanner from '../home/PromoBanner'; // ซ่อนแบนเนอร์โปรโมชันหน้าแรกชั่วคราว
import StatsSection from '../home/StatsSection';
import ReviewsSection from '../home/ReviewsSection';
import FooterSection from '../home/FooterSection';
import ScrollReveal, { StaggerChildren } from '../common/ScrollReveal';
import PageTransition from '../common/PageTransition';

function HomePage({ user, isAuthReady, onLoginClick, mobileMenuOpen, heroImageUrl, heroBrightness = 30 }) {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [stats, setStats] = useState({ orders: 0, users: 0, visitors: 0 });
    const [games, setGames] = useState([]);            // lazy fallback grid only (no-sections case)
    const [featuredGames, setFeaturedGames] = useState([]); // featured/recommended games, fetched by ID
    const [gamesCount, setGamesCount] = useState(0);   // total game count for the stats section
    const [marketplaceItems, setMarketplaceItems] = useState([]);
    const [latestReviews, setLatestReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reviewsPage, setReviewsPage] = useState(1);
    const [totalReviews, setTotalReviews] = useState(0);
    const [ratingStats, setRatingStats] = useState({ avg: 0, dist: [0, 0, 0, 0, 0] });
    const [reviewsLoading, setReviewsLoading] = useState(false);
    const [reviewsPageData, setReviewsPageData] = useState({});
    const [heroSearch, setHeroSearch] = useState('');
    // heroImageUrl + heroBrightness now come from App (single shared read) via props.
    const hasCountedVisitor = useRef(false);

    // Homepage sections data
    const [recommendedItems, setRecommendedItems] = useState([]);
    const [marketplaceFeatured, setMarketplaceFeatured] = useState([]);
    const [serviceFeatured, setServiceFeatured] = useState([]);

    const REVIEWS_PER_PAGE = 5;

    // ===== HELPERS =====
    const maskName = (name) => {
        if (!name) return 'ผู้ใช้xxx';
        if (name.length <= 4) return name + 'xxx';
        return name.substring(0, 4) + 'xxx';
    };

    // ===== HANDLERS =====

    const fetchReviewsPage = async (page, isCancelled) => {
        if (!db || !isFirebaseReady) return;
        if (reviewsPageData[page]) { setLatestReviews(reviewsPageData[page]); setReviewsPage(page); return; }
        setReviewsLoading(true);
        try {
            let data;
            if (page === 1) {
                // Page 1 loads for EVERY visitor on every home mount — cache it so SPA
                // remounts and quick revisits cost 0 reads. createdAt is flattened to
                // millis so it survives the localStorage JSON round-trip (ReviewsSection
                // already falls back to new Date(value) when .toDate is absent).
                data = await getCachedQuery('home_reviews_p1', 10 * 60 * 1000, async () => {
                    const snap = await getDocs(query(collection(db, "reviews"), orderBy("createdAt", "desc"), limit(REVIEWS_PER_PAGE)));
                    return snap.docs.map(d => {
                        const r = { id: d.id, ...d.data() };
                        if (r.createdAt?.toMillis) r.createdAt = r.createdAt.toMillis();
                        return r;
                    });
                });
            } else {
                // Deeper pages are user-driven ("load more") — left uncached on purpose.
                const prev = reviewsPageData[page - 1];
                if (prev?.length > 0) {
                    const lastDoc = await getDoc(doc(db, "reviews", prev[prev.length - 1].id));
                    const snap = await getDocs(query(collection(db, "reviews"), orderBy("createdAt", "desc"), startAfter(lastDoc), limit(REVIEWS_PER_PAGE)));
                    data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                } else { setReviewsLoading(false); return; }
            }
            if (isCancelled?.()) return;
            setReviewsPageData(prev => ({ ...prev, [page]: data }));
            setLatestReviews(data); setReviewsPage(page);
        } catch (e) { console.error("Reviews error:", e); }
        if (isCancelled?.()) return;
        setReviewsLoading(false);
    };

    const nextReviewsPage = () => { if (reviewsPage < Math.ceil(totalReviews / REVIEWS_PER_PAGE)) fetchReviewsPage(reviewsPage + 1); };
    const prevReviewsPage = () => {
        if (reviewsPage > 1) { if (reviewsPageData[reviewsPage - 1]) { setLatestReviews(reviewsPageData[reviewsPage - 1]); setReviewsPage(reviewsPage - 1); } else fetchReviewsPage(reviewsPage - 1); }
    };
    // Data-based last-page check: the paged orderBy('createdAt') query can exclude
    // docs missing createdAt, so the aggregate totalReviews can overcount and let a
    // 'next' load an empty page. Treat the page as last when the fetched batch
    // returned fewer than a full page.
    const isLastReviewsPage = latestReviews.length < REVIEWS_PER_PAGE;

    // ===== SEO =====
    useEffect(() => {
        document.title = "Fastlane Lv. - บริการรับจ้างเล่นเกมออนไลน์อันดับ 1 ในประเทศไทย";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute('content', 'บริการรับจ้างเล่นเกมออนไลน์ทั่วโลก อันดับ 1 ในประเทศไทย มีประสบการณ์มากกว่า 10 ปี ราคาถูก ปลอดภัย 100%');
        return () => { document.querySelectorAll('script[type="application/ld+json"]').forEach(s => s.remove()); };
    }, [stats.orders]);

    // ===== VISITOR COUNT =====
    const incrementVisitorCount = async () => {
        try {
            if (hasCountedVisitor.current) return;
            const today = new Date().toDateString();
            if (localStorage.getItem('visitor_last_count_date') === today) { hasCountedVisitor.current = true; return; }
            // Count via a callable so logged-out visitors are counted without a direct
            // client write to the stats doc (which anonymous clients can't write). Waits for FCM's
            // VAPID key first: fired at page load it re-keyed (killed) this device's push token daily.
            await whenSafeForCallables();
            await httpsCallable(functions, 'recordVisit')();
            hasCountedVisitor.current = true;
            localStorage.setItem('visitor_last_count_date', today);
        } catch (e) { console.error("Visitor error:", e); }
    };

    // ===== DATA FETCHING =====
    // All reads here run for EVERY visitor (incl. anonymous) on the highest-traffic
    // page, so they are one-time getDocs / aggregation reads rather than persistent
    // whole-collection listeners. Nothing here needs to be realtime.
    useEffect(() => {
        if (!isAuthReady || !db || !isFirebaseReady) return;
        setLoading(true);
        let cancelled = false;
        incrementVisitorCount();

        // Stats — one-time cached read (homepage doesn't need live counts, and a live
        // listener re-read for every viewer on each visitor-count write). 10-min TTL
        // means the visitor/user counters can lag by up to 10 minutes — fine for a
        // marketing stats strip.
        getCachedDoc('public/data/stats/website', 10 * 60 * 1000).then(data => {
            if (cancelled || !data) return;
            setStats(prev => ({ ...prev, visitors: data.visitors || 0, users: data.totalUsers || 0, orders: data.totalOrders || prev.orders || 0 }));
        }).catch(e => console.error(e));

        // Games count + review aggregates (total/avg/★-distribution) change slowly, but
        // were ~7 count() aggregations on EVERY homepage load — the busiest route. Cache
        // the computed result in localStorage (short TTL) so a visitor's repeat views and
        // in-app navigations back to home don't re-run the aggregations every time.
        const HS_KEY = 'fl_home_stats_v1';
        const HS_TTL = 15 * 60 * 1000; // 15 minutes
        let hsCached = null;
        try {
            const raw = localStorage.getItem(HS_KEY);
            if (raw) { const p = JSON.parse(raw); if (Date.now() - p.ts < HS_TTL) hsCached = p.data; }
        } catch (_) { }

        if (hsCached) {
            setGamesCount(hsCached.gamesCount || 0);
            setTotalReviews(hsCached.totalReviews || 0);
            if (hsCached.ratingStats) setRatingStats(hsCached.ratingStats);
        } else {
            (async () => {
                // PREFERRED: one cached read of the CF-maintained summary doc replaces
                // the 7 count() aggregations below. Falls back to the aggregation
                // battery while the doc/functions are not deployed yet (or the doc is
                // missing fields) — the null/miss is cached too, so the probe costs at
                // most one read per TTL.
                let hs = null;
                try { hs = await getCachedDoc('public/data/stats/homepage', HS_TTL); } catch (_) { hs = null; }
                if (hs && typeof hs.gamesCount === 'number' && typeof hs.reviewTotal === 'number' && hs.reviewByStar) {
                    if (cancelled) return;
                    setGamesCount(hs.gamesCount || 0);
                    const total = hs.reviewTotal;
                    setTotalReviews(total);
                    if (total > 0) {
                        const counts = [1, 2, 3, 4, 5].map(star => hs.reviewByStar[String(star)] || 0); // idx0=1★ .. idx4=5★
                        // Reviews with no rating field default to 5★ (matches the old `rating || 5`).
                        counts[4] += Math.max(0, total - counts.reduce((a, b) => a + b, 0));
                        const sum = counts.reduce((acc, c, i) => acc + (i + 1) * c, 0);
                        const dist = [5, 4, 3, 2, 1].map(star => Math.round((counts[star - 1] / total) * 100));
                        const avg = typeof hs.reviewAvg === 'number' ? parseFloat(hs.reviewAvg.toFixed(1)) : parseFloat((sum / total).toFixed(1));
                        setRatingStats({ avg, dist });
                    }
                    return;
                }
                // FALLBACK: summary doc not available yet — original aggregation path.
                try {
                    const reviewsCol = collection(db, "reviews");
                    const [gamesSnap, totalSnap, ...starSnaps] = await Promise.all([
                        getCountFromServer(collection(db, "public/data/games")),
                        getCountFromServer(reviewsCol),
                        ...[1, 2, 3, 4, 5].map(r => getCountFromServer(query(reviewsCol, where('rating', '==', r)))),
                    ]);
                    if (cancelled) return;
                    const gamesCount = gamesSnap.data().count;
                    setGamesCount(gamesCount);
                    const total = totalSnap.data().count;
                    const counts = starSnaps.map(s => s.data().count); // idx0=1★ .. idx4=5★
                    // Reviews with no rating field default to 5★ (matches the old `rating || 5`).
                    counts[4] += Math.max(0, total - counts.reduce((a, b) => a + b, 0));
                    setTotalReviews(total);
                    let ratingStats = null;
                    if (total > 0) {
                        const sum = counts.reduce((acc, c, i) => acc + (i + 1) * c, 0);
                        const dist = [5, 4, 3, 2, 1].map(star => Math.round((counts[star - 1] / total) * 100));
                        ratingStats = { avg: parseFloat((sum / total).toFixed(1)), dist };
                        setRatingStats(ratingStats);
                    }
                    try { localStorage.setItem(HS_KEY, JSON.stringify({ data: { gamesCount, totalReviews: total, ratingStats }, ts: Date.now() })); } catch (_) { }
                } catch (e) { console.error("Home stats error:", e); }
            })();
        }
        fetchReviewsPage(1, () => cancelled);

        // Homepage sections — cached one-time read (admin-curated config, rarely
        // changes; ~30 docs that used to be re-read on every home remount).
        getCachedQuery('homepage_sections', 10 * 60 * 1000, async () => {
            const snap = await getDocs(query(collection(db, "homepage_sections"), orderBy("order", "asc")));
            return snap.docs.map(d => ({ id: d.id, ...d.data() }));
        }).then(all => {
            if (cancelled) return;
            setRecommendedItems(all.filter(i => i.section === 'recommended').sort((a, b) => a.order - b.order));
            setMarketplaceFeatured(all.filter(i => i.section === 'marketplace').sort((a, b) => a.order - b.order));
            setServiceFeatured(all.filter(i => i.section === 'services').sort((a, b) => a.order - b.order));
            setLoading(false);
        }).catch(() => { if (!cancelled) setLoading(false); });

        return () => { cancelled = true; };
    }, [isAuthReady]); // eslint-disable-line react-hooks/exhaustive-deps

    // Resolve featured marketplace items by ID — was streaming the WHOLE marketplace
    // collection per visitor just to pick out a handful of featured items.
    useEffect(() => {
        if (!db || !isFirebaseReady) return;
        const ids = [...new Set(marketplaceFeatured.map(e => e.itemId).filter(Boolean))];
        if (ids.length === 0) { setMarketplaceItems([]); return; }
        let cancelled = false;
        (async () => {
            try {
                // Cached per-doc reads (10-min TTL) so home remounts don't re-bill the
                // same handful of featured docs. Cards render plain fields only, so the
                // localStorage JSON round-trip is safe.
                const datas = await Promise.all(ids.map(id => getCachedDoc(`marketplace/${id}`, 10 * 60 * 1000).catch(() => null)));
                if (!cancelled) setMarketplaceItems(ids.map((id, i) => datas[i] ? { id, ...datas[i] } : null).filter(Boolean));
            } catch (e) { console.error('Featured marketplace error:', e); }
        })();
        return () => { cancelled = true; };
    }, [marketplaceFeatured]);

    // Resolve featured/recommended GAMES by ID — was streaming the WHOLE games
    // collection (118 docs) per visitor just to pick out a handful of featured cards.
    useEffect(() => {
        if (!db || !isFirebaseReady) return;
        const ids = [...new Set([...recommendedItems, ...serviceFeatured].map(e => e.itemId).filter(Boolean))];
        if (ids.length === 0) { setFeaturedGames([]); return; }
        let cancelled = false;
        (async () => {
            try {
                // Cached per-doc reads (10-min TTL) — games change rarely; see note above.
                const datas = await Promise.all(ids.map(id => getCachedDoc(`public/data/games/${id}`, 10 * 60 * 1000).catch(() => null)));
                if (!cancelled) setFeaturedGames(datas.map((data, i) => data ? { id: ids[i], ...data } : null).filter(Boolean));
            } catch (e) { console.error('Featured games error:', e); }
        })();
        return () => { cancelled = true; };
    }, [recommendedItems, serviceFeatured]);

    // Fallback grid — only when NO homepage sections are configured, lazily load a
    // few games. In the normal case (sections present) this never runs, so a visit
    // no longer reads the whole games collection.
    useEffect(() => {
        if (loading || !db || !isFirebaseReady) return;
        const noSections = recommendedItems.length === 0 && marketplaceFeatured.length === 0 && serviceFeatured.length === 0;
        if (!noSections || games.length > 0) return;
        let cancelled = false;
        getDocs(query(collection(db, "public/data/games"), orderBy("order", "asc"), limit(12))).then(s => {
            if (!cancelled) setGames(s.docs.map(d => ({ id: d.id, ...d.data() })));
        }).catch(() => { });
        return () => { cancelled = true; };
    }, [loading, recommendedItems.length, marketplaceFeatured.length, serviceFeatured.length, games.length]);

    // ===== RESOLVE featured items to actual data =====
    const resolvedRecommended = useMemo(() => {
        return recommendedItems.map(entry => featuredGames.find(g => g.id === entry.itemId)).filter(Boolean);
    }, [recommendedItems, featuredGames]);

    const resolvedMarketplace = useMemo(() => {
        return marketplaceFeatured.map(entry => marketplaceItems.find(m => m.id === entry.itemId)).filter(Boolean);
    }, [marketplaceFeatured, marketplaceItems]);

    const resolvedServices = useMemo(() => {
        return serviceFeatured.map(entry => featuredGames.find(g => g.id === entry.itemId)).filter(Boolean);
    }, [serviceFeatured, featuredGames]);

    const handleSelectGame = (game) => {
        navigate(`/game/${game.id}`);
    };

    const handleMarketplaceClick = () => {
        navigate('/marketplace');
    };

    // ===== SHARED CARD COMPONENT =====
    const GameCard = ({ game }) => (
        <div
            onClick={() => handleSelectGame(game)}
            className="group cursor-pointer bg-white rounded-2xl border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:border-gray-200"
        >
            <div className="aspect-[4/3] relative overflow-hidden bg-gray-100">
                <img src={game.imageUrl} alt={game.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" decoding="async" />
            </div>
            <div className="p-4">
                <p className="text-xs text-blue-500 font-bold uppercase tracking-wider mb-1">{game.name?.split(' ')[0]}</p>
                <h3 className="text-gray-900 font-bold text-sm leading-snug mb-3 line-clamp-2">
                    {game.name}
                </h3>
                <div className="flex items-center gap-2 mb-3">
                    <BrandBadge />
                    <div className="flex items-center gap-0.5 ml-auto">
                        <FaStar className="text-amber-400 text-[10px]" />
                        <span className="text-xs text-gray-500">4.9</span>
                    </div>
                </div>
                <div className="flex items-center justify-between">
                    <div>
                        <span className="text-[10px] text-gray-400">{t('home.startingPrice')}</span>
                        <p className="text-gray-900 font-black text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>฿100</p>
                    </div>
                    <button className="px-4 py-2 rounded-lg text-xs font-semibold text-blue-600 border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors">
                        {t('home.viewDetails')}
                    </button>
                </div>
            </div>
        </div>
    );

    const MarketplaceCard = ({ item }) => (
        <div
            onClick={handleMarketplaceClick}
            className="group cursor-pointer bg-white rounded-2xl border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:border-gray-200"
        >
            <div className="aspect-[4/3] relative overflow-hidden bg-gray-100">
                {item.images?.[0] ? (
                    <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" decoding="async" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center"><FaStore className="text-4xl text-gray-300" /></div>
                )}
                <div className="absolute top-2.5 left-2.5">
                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${item.type === 'account' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-purple-50 text-purple-600 border-purple-200'}`}>
                        {item.type === 'account' ? t('home.gameAccount') : t('home.gameItem')}
                    </span>
                </div>
            </div>
            <div className="p-4">
                <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">{item.game}</p>
                <h3 className="text-gray-900 font-bold text-sm leading-snug mb-3 line-clamp-2">
                    {item.title}
                </h3>
                <div className="flex items-center gap-2 mb-3">
                    <BrandBadge />
                    <div className="flex items-center gap-0.5 ml-auto">
                        <FaStar className="text-amber-400 text-[10px]" />
                        <span className="text-xs text-gray-500">4.9</span>
                    </div>
                </div>
                <div className="flex items-center justify-between">
                    <div>
                        <span className="text-[10px] text-gray-400">{t('home.price')}</span>
                        <p className="text-gray-900 font-black text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>฿{new Intl.NumberFormat('th-TH').format(item.price || 0)}</p>
                    </div>
                    <button className="px-4 py-2 rounded-lg text-xs font-semibold text-white bg-blue-500 hover:bg-blue-600 transition-colors">
                        {t('home.viewDetails')}
                    </button>
                </div>
            </div>
        </div>
    );

    // ===== RENDER =====
    return (
        <div className="min-h-screen bg-white">

            {/* ===== HERO SECTION ===== */}
            <HeroSection heroSearch={heroSearch} setHeroSearch={setHeroSearch} heroImageUrl={heroImageUrl} heroBrightness={heroBrightness} />
            <ActivityTicker />

            {/* ===== OFFICIAL SPONSOR (self-fetches; renders only when an active sponsor exists) ===== */}
            <SponsorBand />

            {/* ===== PARTNER SHOPS (ร้านค้าพันธมิตร — self-fetches; renders only when a visible partner exists) ===== */}
            <PartnerBand />

            {/* ===== PROMOTION BANNER (ซ่อนชั่วคราว — เปิดกลับมาโดยเอาคอมเมนต์ออก) ===== */}
            {/* <PromoBanner /> */}

            {/* ===== RECOMMENDED (รายการแนะนำล่าสุด) ===== */}
            {resolvedRecommended.length > 0 && (
                <section className="py-8 md:py-10 px-4 sm:px-6 lg:px-8 bg-white">
                    <div className="max-w-7xl mx-auto">
                        <ScrollReveal animation="fadeLeft">
                            <div className="flex items-center justify-between mb-10">
                                <div className="flex items-center gap-3">
                                    <FaFire className="text-orange-500 text-xl" />
                                    <h2 className="text-2xl md:text-3xl font-black text-gray-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                        {t('home.recommended')}
                                    </h2>
                                </div>
                                <Link to="/marketplace" className="text-blue-500 text-sm font-medium flex items-center gap-1 hover:text-blue-600 transition-colors">
                                    {t('home.viewAll')} <FaArrowRight className="text-xs" />
                                </Link>
                            </div>
                        </ScrollReveal>
                        <StaggerChildren animation="fadeUp" staggerDelay={80} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
                            {resolvedRecommended.map((game) => (
                                <GameCard key={game.id} game={game} />
                            ))}
                        </StaggerChildren>
                    </div>
                </section>
            )}

            {/* ===== MARKETPLACE (ตลาดซื้อขาย) ===== */}
            {resolvedMarketplace.length > 0 && (
                <section className="py-8 md:py-10 px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(180deg, #F8FAFF 0%, #EEF2FF 100%)' }}>
                    <div className="max-w-7xl mx-auto">
                        <ScrollReveal animation="fadeLeft">
                            <div className="flex items-center justify-between mb-10">
                                <div className="flex items-center gap-3">
                                    <FaShoppingCart className="text-blue-500 text-xl" />
                                    <h2 className="text-2xl md:text-3xl font-black text-gray-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                        {t('home.marketplace')}
                                    </h2>
                                </div>
                                <Link to="/marketplace" className="text-blue-500 text-sm font-medium flex items-center gap-1 hover:text-blue-600 transition-colors">
                                    {t('home.viewAllShort')} <FaArrowRight className="text-xs" />
                                </Link>
                            </div>
                        </ScrollReveal>
                        <StaggerChildren animation="fadeUp" staggerDelay={80} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
                            {resolvedMarketplace.map((item) => (
                                <MarketplaceCard key={item.id} item={item} />
                            ))}
                        </StaggerChildren>
                    </div>
                </section>
            )}

            {/* ===== SERVICES (บริการเกมแนะนำ) ===== */}
            {resolvedServices.length > 0 && (
                <section id="games-section" className="py-8 md:py-10 px-4 sm:px-6 lg:px-8" style={{ background: '#F8FAFC' }}>
                    <div className="max-w-7xl mx-auto">
                        <ScrollReveal animation="fadeLeft">
                            <div className="flex items-center justify-between mb-10">
                                <div className="flex items-center gap-3">
                                    <FaGamepad className="text-purple-500 text-xl" />
                                    <div>
                                        <h2 className="text-2xl md:text-3xl font-black text-gray-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                            {t('home.services')}
                                        </h2>
                                        <p className="text-gray-500 text-sm mt-1">{t('home.servicesDesc')}</p>
                                    </div>
                                </div>
                                <Link to="/games" className="text-blue-500 text-sm font-medium flex items-center gap-1 hover:text-blue-600 transition-colors">
                                    {t('home.viewAll')} <FaArrowRight className="text-xs" />
                                </Link>
                            </div>
                        </ScrollReveal>
                        <StaggerChildren animation="fadeUp" staggerDelay={80} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
                            {resolvedServices.map((game) => (
                                <GameCard key={game.id} game={game} />
                            ))}
                        </StaggerChildren>
                    </div>
                </section>
            )}

            {/* ===== FALLBACK: Show all games if no sections configured yet ===== */}
            {loading && (
                <section className="py-8 md:py-10 px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-center py-20"><Loader /></div>
                </section>
            )}

            {!loading && resolvedRecommended.length === 0 && resolvedMarketplace.length === 0 && resolvedServices.length === 0 && games.length > 0 && (
                <section id="games-section" className="py-8 md:py-10 px-4 sm:px-6 lg:px-8" style={{ background: '#F8FAFC' }}>
                    <div className="max-w-7xl mx-auto">
                        <div className="mb-10">
                            <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                {t('home.chooseService')}
                            </h2>
                            <p className="text-gray-500">{t('home.servicesDesc')}</p>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
                            {games.slice(0, 10).map(game => (
                                <GameCard key={game.id} game={game} />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ===== STATS ===== */}
            <StatsSection stats={stats} gamesCount={gamesCount || games.length} />

            {/* ===== REVIEWS ===== */}
            <ReviewsSection
                latestReviews={latestReviews}
                totalReviews={totalReviews}
                ratingStats={ratingStats}
                reviewsPage={reviewsPage}
                reviewsLoading={reviewsLoading}
                onNextPage={nextReviewsPage}
                onPrevPage={prevReviewsPage}
                isLastPage={isLastReviewsPage}
                reviewsPerPage={REVIEWS_PER_PAGE}
                maskName={maskName}
            />

            {/* ===== FOOTER ===== */}
            <FooterSection />

            {/* Live Chat */}

        </div>
    );
}

export default HomePage;
